import React, { useState, useMemo } from 'react'
import SearchFilterIcon from '../../assets/icons/search-filter'
import PageWrapper from '../../components/PageWrapper'
import CustomTable from '../../components/Table'
import profileIcon from '../../assets/icons/profile-icon.svg'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableData from '../../components/Table/TableData'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import CustomTableRow from '../../components/Table/TableRow'
import TablePagination from '../../components/Table/Pagination'
import SearchIcon from '../../assets/icons/search.svg'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'
import Empty from '../../assets/icons/Empty'
import { AppFeedback } from '../../interfaces/feedback'
import FeedbackService from '../../api/feedback-service'
import AddButton from '../../components/IconButton'
import addIcon from '../../assets/icons/Addimg.svg'
import AddFeedback from './Addfeedback'
import { getLocal } from '../../utils/local'
import FeedbackDetailsModal from '../../components/Feedback/FeedbackDetailsModal'
import Metadata from '../../components/Metadata'

  const feedbackMetadata = {
    title: 'Feedback - Safelto',
    description: 'View and manage all feedback from residents and staff. Complete feedback management solution for residential communities.',
    keywords: ['feedback', 'resident feedback', 'staff feedback', 'property management', 'community feedback'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/feedback',
    type: 'website' as 'website'
  }

  const Feedback = () => {
    const tableHeadData = ['User', 'Time', 'Position', 'Ratings', 'Comment', '']
    const itemsPerPage = 6

    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState({ label: 'All', value: 'All' })
    const [currentPage, setCurrentPage] = useState(1)
    const [showAddFeedback, setShowAddFeedback] = useState(false)
    const [selectedFeedback, setSelectedFeedback] = useState<AppFeedback | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const securityStatuses = [
      { label: 'All', value: 'All' },
      { label: 'Resident', value: 'resident' },
      { label: 'Staff', value: 'staff' },
    ]

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
      setCurrentPage(1)
    }

    const handleStatusSelect = (status: { label: string; value: string }) => {
      setStatusFilter(status)
      setCurrentPage(1)
    }

    const handleViewFeedback = (feedback: AppFeedback) => {
      setSelectedFeedback(feedback)
      setIsModalOpen(true)
    }

    const handleCloseModal = () => {
      setIsModalOpen(false)
      setSelectedFeedback(null)
    }

    const estate = getLocal('estate')
    const estateId = estate?.id

    const {
      data: feedbackResponse,
      isLoading,
      error,
    } = FeedbackService.useGetFeedbacks({ page: 1, limit: 50, estateId })

    const feedbacks = feedbackResponse?.results || []

    const filteredFeedbacks = useMemo(() => {
      return feedbacks.filter((feedback) => {
        const matchesSearch =
          searchTerm === '' ||
          feedback.submittedById.fullName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          feedback.content.toLowerCase().includes(searchTerm.toLowerCase())

        const matchesFilter =
          statusFilter.value === 'All' ||
          feedback.submittedByType.toLowerCase() === statusFilter.value

        return matchesSearch && matchesFilter
      })
    }, [feedbacks, searchTerm, statusFilter])

    const totalPages = Math.ceil(filteredFeedbacks.length / itemsPerPage)
    const paginatedFeedbacks = filteredFeedbacks.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    )

    const formatTime = (date: string) => {
      const d = new Date(date)
      const dateStr = d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
      const timeStr = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      return `${dateStr}. ${timeStr}`
    }

    const renderStars = (rating: number) => (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-xl ${star <= rating ? 'text-orange-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        ))}
      </div>
    )

    return (
      <>
        <PageWrapper title="Feedback" description="View all feedbacks">
          <Metadata {...feedbackMetadata} />
        {showAddFeedback ? (
          <AddFeedback onBack={() => setShowAddFeedback(false)} />
        ) : (
          <>
            {/* HEADER */}
            <div className="py-5 mb-6">
              <AddButton
                handleModalOpen={() => setShowAddFeedback(true)}
                buttonStyle="w-full sm:w-[222px]"
                icon={<img src={addIcon} alt="Add icon" />}
              >
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium">Add Feedback</p>
                  <p className="text-xs text-gray-600">Add new feedback</p>
                </div>
              </AddButton>
            </div>

            {/* SEARCH */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex flex-col sm:flex-row gap-2.5 mb-6"
            >
              <div className="w-full flex bg-gray-100 rounded-lg items-center px-2">
                <img src={SearchIcon} alt="Search" />
                <input
                  placeholder="Search"
                  className="py-2.5 w-full bg-transparent outline-none"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="relative flex items-center gap-2 p-2.5 group">
                <p>{statusFilter.label}</p>
                <div className="bg-gray-100 w-8 h-8 flex items-center justify-center rounded-lg">
                  <SearchFilterIcon />
                </div>

                <div className="absolute top-12 right-0 hidden group-hover:block w-40 bg-white shadow rounded">
                  {securityStatuses.map((status) => (
                    <p
                      key={status.value}
                      onClick={() => handleStatusSelect(status)}
                      className="p-3 hover:bg-gray-200 cursor-pointer"
                    >
                      {status.label}
                    </p>
                  ))}
                </div>
              </div>
            </form>

            {isLoading ? (
              <ViewStaffsSkeletonLoader />
            ) : error || filteredFeedbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[250px] gap-3">
                <Empty />
                <p className="text-sm">
                  {error
                    ? 'Error loading feedback'
                    : filteredFeedbacks.length === 0 && feedbacks.length > 0
                      ? 'No feedback matches your search'
                      : 'No feedback available yet'}
                </p>
              </div>
            ) : (
              <>
                {/* MOBILE */}
                <div className="sm:hidden flex flex-col gap-3">
                  {paginatedFeedbacks.map((feedback) => (
                    <div
                      key={feedback.id}
                      className="p-3 rounded-xl border bg-white"
                      onClick={() => handleViewFeedback(feedback)}
                    >
                      <p className="font-medium truncate">
                        {feedback.submittedById.fullName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {feedback.submittedById.email}
                      </p>
                      <p className="text-xs mt-1">{formatTime(feedback.createdAt)}</p>
                      <div className="mt-1">{renderStars(feedback.rating)}</div>
                      <p className="text-sm mt-1 line-clamp-3">{feedback.content}</p>
                    </div>
                  ))}
                </div>

                {/* DESKTOP */}
                <div className="hidden sm:block w-full">
                  <CustomTable className="w-full table-auto">
                    <CustomTableHead dataList={tableHeadData} />
                    <CustomTableBody>
                      {paginatedFeedbacks.map((feedback) => (
                        <CustomTableRow
                          key={feedback.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleViewFeedback(feedback)}
                        >
                          <CustomTableData className="px-4 max-w-[150px] truncate">
                            <div className="flex items-center gap-3">
                              <img
                                src={feedback.submittedById.avatar || profileIcon}
                                alt="Profile"
                                className="w-10 h-10 rounded-full"
                              />
                              <div>
                                <p>{feedback.submittedById.fullName}</p>
                                <p className="text-sm text-gray-500">
                                  {feedback.submittedById.email}
                                </p>
                              </div>
                            </div>
                          </CustomTableData>

                          <CustomTableData className="px-4 max-w-[150px] truncate">
                            {formatTime(feedback.createdAt)}
                          </CustomTableData>

                          <CustomTableData className="px-4 max-w-[150px] truncate">
                            {feedback.submittedByType}
                          </CustomTableData>

                          <CustomTableData className="px-4 max-w-[150px] truncate">
                            {renderStars(feedback.rating)}
                          </CustomTableData>

                          <CustomTableData className="px-4 max-w-[150px] truncate">
                            <div className="truncate max-w-[200px]">
                              {feedback.content}
                            </div>
                          </CustomTableData>

                          <CustomTableDataCTA text="...">
                            {' '}
                            <div className="flex flex-col bg-white shadow rounded w-40">
                              {' '}
                              <p className="p-3 text-red-500">Delete feedback</p>{' '}
                              <p className="p-3">View details</p>{' '}
                            </div>{' '}
                          </CustomTableDataCTA>
                        </CustomTableRow>
                      ))}
                    </CustomTableBody>
                  </CustomTable>
                </div>
              </>
            )}

            {filteredFeedbacks.length > itemsPerPage && (
              <TablePagination
                totalPages={totalPages}
                currPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            )}
          </>
        )}
      </PageWrapper>

      <FeedbackDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        feedback={selectedFeedback}
      />
    </>
  )
}

export default Feedback